<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import DashboardLayouts from '@/Layouts/DashboardLayouts.vue';
import BreadCrumb from '@/components/BreadCrumb.vue';
import Loader from '@/components/Loader.vue';
import { apiService } from '@/services/ApiService';
import { API_ROUTES } from '@/services/ApiRoutes';
import TickIcon from '@/views/icons/TickIcon.vue';
import EllipseIcon from '@/views/icons/EllipseIcon.vue';
import LeftArrow from '@/views/icons/LeftArrow.vue';
import RightArrow from '@/views/icons/RightArrow.vue';
import Modal from '@/components/Modal.vue';
import { useToast } from '@/utils/toast';
import { currentWebStore } from '@/services/Store';

const router = useRouter();
const route = useRoute();
const toast = useToast();
const webStore = currentWebStore();

const plans = ref([]);
const isPending = ref(false);
const carousel = ref(null);
const showSubscribeModal = ref(false);
const selectedPlan = ref(null);
const selectedAddons = ref([]);
const selectedAddonsPerPlan = ref({});
const isSubscribing = ref(false);
const selectedWebsite = ref(null);
const showWebsiteSelector = ref(false);
const websites = ref([]);

const scrollLeft = () => {
  if (carousel.value) {
    carousel.value.scrollBy({ left: -300, behavior: 'smooth' });
  }
};

const scrollRight = () => {
  if (carousel.value) {
    carousel.value.scrollBy({ left: 300, behavior: 'smooth' });
  }
};

onMounted(async () => {
  isPending.value = true;
  try {
    // Load plans
    const response = await apiService.get(API_ROUTES.plans, { all: true });
    plans.value = Array.isArray(response.data?.data)
      ? response.data.data
      : Array.isArray(response.data)
      ? response.data
      : [];

    // Load websites for admin
    const websitesResponse = await apiService.get(API_ROUTES.websites, { per_page: 1000 });
    websites.value = Array.isArray(websitesResponse.data?.data)
      ? websitesResponse.data.data
      : Array.isArray(websitesResponse.data)
      ? websitesResponse.data
      : [];

    // Initialize selectedWebsite from localStorage if available
    const storedId = localStorage.getItem('admin_selected_website_id');
    if (storedId) {
      selectedWebsite.value = storedId;
    }

    // If no website selected, show selector for direct access
    if (!website_id.value) {
      showWebsiteSelector.value = true;
    }
  } catch (error) {
    console.error('Error fetching data:', error);
  } finally {
    isPending.value = false;
  }
});

// Function to select website and store in localStorage (no URL params for security)
const selectWebsite = (websiteId) => {
  console.log('Selecting website:', websiteId); // Debug log
  localStorage.setItem('admin_selected_website_id', websiteId.toString());
  webStore.handleWebId(+websiteId);
  
  // Force reactivity update by triggering a re-computation
  selectedWebsite.value = websiteId;
  
  showWebsiteSelector.value = false;
  // No URL update for security - keep website_id hidden
};

// Smart website_id handling - check localStorage and store only (no URL params for security)
const website_id = computed(() => {
  // Force reactivity by including selectedWebsite in the computation
  if (selectedWebsite.value) {
    return selectedWebsite.value.toString();
  }
  
  // Priority: localStorage > store (no URL params for security)
  const storedId = localStorage.getItem('admin_selected_website_id');
  if (storedId) {
    return storedId;
  }
  
  return webStore.getWebId?.toString();
});

// Computed for display purposes
const currentWebsite = computed(() => {
  if (!website_id.value) return null;
  const foundWebsite = websites.value.find(w => w.id.toString() === website_id.value.toString());
  console.log('Current website lookup:', { website_id: website_id.value, foundWebsite }); // Debug log
  return foundWebsite;
});

// Computed to filter only inactive websites for selection
const inactiveWebsites = computed(() => {
  return websites.value.filter(w => !w.active);
});
const openSubscribeModal = (plan) => {
  selectedPlan.value = plan;
  // Use the selected addons for this plan, or empty array
  selectedAddons.value = selectedAddonsPerPlan.value[plan.id] || [];
  showSubscribeModal.value = true;
};

const handleSubscribe = async () => {
  if (!website_id.value || !selectedPlan.value) {
    toast.error('Please select a website first');
    return;
  }
  
  isSubscribing.value = true;
  try {
    const payload = {
      website_id: website_id.value,
      plan_id: selectedPlan.value.id,
    };
    if (selectedAddons.value.length) {
      payload.addon_ids = selectedAddons.value;
    }
    
    const response = await apiService.post('/admin/subscriptions/create', payload);
    showSubscribeModal.value = false;
    toast.success(response?.data?.message || 'Plan subscribed successfully!');
    
    // Show success message for 1 second then navigate
    setTimeout(() => {
      localStorage.removeItem('admin_selected_website_id');
      router.push('/website');
    }, 1000);
  } catch (error) {
    toast.error(error?.response?.data?.message || 'Error subscribing plan');
    console.error('Subscribe error:', error);
  } finally {
    isSubscribing.value = false;
  }
};
</script>

<template>
  <DashboardLayouts>
    <div class="flex justify-between items-center mb-3 px-2">
      <BreadCrumb heading="Admin Plans" page="Admin Plans" />
      <div v-if="currentWebsite" class="flex items-center gap-3">
        <div class="flex items-center gap-3 bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-2 rounded-lg border border-blue-200">
          <div class="flex items-center gap-2">
            <div class="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
            <span class="text-sm font-medium text-gray-700">Selected Website:</span>
          </div>
          <div class="flex items-center gap-2">
            <span class="font-semibold text-[#40008A]">{{ currentWebsite.url }}</span>
            
          </div>
        </div>

        <button 
          @click="showWebsiteSelector = true" 
          class="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors duration-200"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"></path>
          </svg>
          Change
        </button>
      </div>
    </div>

  
    <Modal :isOpen="showWebsiteSelector" @close="showWebsiteSelector = false" title="🌐 Select Website">
      <div class="max-h-[500px] overflow-y-auto">
        <div class="mb-6">
          <p class="text-gray-700 font-medium mb-2">Choose an inactive website to subscribe a plan for:</p>
          <div class="w-full h-px bg-gradient-to-r from-blue-200 via-purple-200 to-pink-200"></div>
        </div>
        
        <div v-if="!inactiveWebsites.length" class="text-center py-12">
          <div class="mb-4">
            <svg class="w-16 h-16 mx-auto text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <p class="text-gray-600 text-lg font-medium">All websites are active! 🎉</p>
          <p class="text-gray-500 text-sm mt-2">Every website already has an active subscription.</p>
        </div>
        
        <div v-else class="space-y-4">
          <div 
            v-for="website in inactiveWebsites" 
            :key="website.id"
            class="group relative overflow-hidden bg-white rounded-2xl border border-gray-100 cursor-pointer transition-all duration-300 hover:border-blue-200 hover:shadow-xl hover:shadow-blue-50 transform hover:-translate-y-0.5"
            :class="{ 
              'ring-2 ring-blue-500 ring-opacity-50 border-blue-300 shadow-lg shadow-blue-100/50 bg-gradient-to-br from-blue-50/50 to-indigo-50/30': website_id.toString() === website.id.toString()
            }"
            @click="selectWebsite(website.id)"
          >
            <!-- Subtle top accent line -->
            <div class="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500" 
                 :class="{ 'opacity-100': website_id.toString() === website.id.toString(), 'opacity-0 group-hover:opacity-60': website_id.toString() !== website.id.toString() }"
                 style="transition: opacity 0.3s ease;"></div>

            <!-- Selection check badge -->
            <div v-if="website_id.toString() === website.id.toString()" 
                 class="absolute top-4 right-4 z-10">
              <div class="w-7 h-7 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg shadow-blue-200">
                <svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
              </div>
            </div>

            <div class="p-6">
              <!-- Header section with icon and URL -->
              <div class="flex items-start gap-4 mb-5">
                <div class="flex-shrink-0">
                  <div class="w-12 h-12 bg-gradient-to-br from-indigo-500 via-purple-600 to-blue-700 rounded-xl flex items-center justify-center shadow-lg shadow-purple-200/50 group-hover:shadow-purple-300/60 transition-shadow duration-300">
                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9"></path>
                    </svg>
                  </div>
                </div>
                
                <div class="flex-1 min-w-0">
                  <h3 class="text-lg font-bold text-gray-900 truncate mb-1 group-hover:text-blue-700 transition-colors duration-200">
                    {{ website.url }}
                  </h3>
                  <div class="flex items-center gap-3">
                    <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase"
                          :class="{
                            'bg-emerald-100 text-emerald-800': website.platform === 'wordpress',
                            'bg-green-100 text-green-800': website.platform === 'shopify',
                            'bg-blue-100 text-blue-800': !['wordpress', 'shopify'].includes(website.platform)
                          }">
                      {{ website.platform }}
                    </span>
                    <div class="flex items-center gap-1.5">
                      <div class="w-1.5 h-1.5 bg-red-400 rounded-full animate-pulse"></div>
                      <span class="text-xs font-medium text-red-600">Inactive</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Details section -->
              <div class="space-y-3 pl-16">
                <!-- Username/Store Name -->
                <div class="flex items-center gap-2.5">
                  <div class="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <svg class="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                    </svg>
                  </div>
                  <span class="text-sm font-medium text-gray-700">{{ website.username || 'N/A' }}</span>
                </div>

                <!-- Owner info -->
                <div v-if="website.user?.name" class="flex items-center gap-2.5">
                  <div class="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <svg class="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                    </svg>
                  </div>
                  <span class="text-sm text-gray-600">Owner: <span class="font-medium text-gray-800">{{ website.user.name }}</span></span>
                </div>
              </div>
            </div>

            <!-- Hover glow effect -->
            <div class="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
            
            <!-- Selection glow effect -->
            <div v-if="website_id.toString() === website.id.toString()" 
                 class="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500/10 via-indigo-500/5 to-purple-500/10 pointer-events-none"></div>
          </div>
        </div>
      </div>
    </Modal>

    <div class="relative mt-6">
      <div v-if="isPending">
        <Loader />
      </div>
      <div
        v-else-if="!Array.isArray(plans) || !plans.length"
        class="font-bold text-center my-4"
      >
        <p>No Data Found!</p>
      </div>
      <div
        v-else
        ref="carousel"
        class="flex gap-4 overflow-x-auto scroll-smooth no-scrollbar pr-10 h-[22rem] pl-1 w-full max-w-full"
      >
        <div
          v-for="(plan, index) in plans"
          :key="plan.id"
          class="planCards w-[250px] flex-shrink-0 overflow-hidden relative"
        >
          <div class="authEllipseTop pointer-events-none">
            <EllipseIcon />
          </div>

          <p class="truncate flex-shrink-0" :title="plan?.name">
            {{ plan?.name }}
          </p>
          <p class="text-[11px] mb-1 line-clamp-3 flex-shrink-0">
            {{ plan?.description }}
          </p>
          <p class="text-[12px] flex-shrink-0">
            ${{ plan?.price ?? 0 }}
            <span class="text-[#475467] text-[9px]"> /per month</span>
          </p>

          <div class="flex justify-center pb-2 mb-3 border-b-2 border-gray-100 flex-shrink-0">
            <button
              class="text-[13px] bg-[#40008A] rounded-full py-1.5 text-white px-10 mt-4 z-10 cursor-pointer"
              @click="openSubscribeModal(plan)"
            >
              Subscribe Plan
            </button>
          </div>

          <p
            v-if="plan?.free_trial_articles > 0 || plan?.free_trial_keywords > 0"
            class="text-[12px] text-gray-600 font-bold my-1 flex-shrink-0"
          >
            {{ plan?.trial_days }}-Days Free Trail
          </p>

          <p class="text-[12px] font-bold my-1 flex-shrink-0">Plan Summary:</p>

          <div class="flex-1 min-h-0 overflow-y-auto custom-scrollbar pr-1">
              <p class="mb-1 text-[12px] flex gap-1">
                <TickIcon />
                {{ plan?.article_count ?? 0 }} articles per month
              </p>
              <p class="mb-1 text-[12px] flex gap-1">
                <TickIcon />
                {{ plan?.keyword ?? 0 }} keywords per month
              </p>
              <p
                v-if="plan?.free_trial_articles > 0"
                class="mb-1 text-[12px] flex gap-1"
              >
                <TickIcon />
                {{ plan?.free_trial_articles }} initial trial articles
              </p>
              <p
                v-if="plan?.free_trial_keywords > 0"
                class="mb-1 text-[12px] flex gap-1"
              >
                <TickIcon />
                {{ plan?.free_trial_keywords }} initial trial keywords
              </p>

              <h1 v-if="plan?.addons && plan.addons.length" class="text-[12px] font-bold my-1 overflow-auto">Add-Ons</h1>
              <div v-if="plan?.addons && plan.addons.length" class="flex flex-col gap-0 mb-1 overflow-auto">
                <div v-for="(addon, aIndex) in plan.addons" :key="addon.id" class="group/addon">
                  <label :for="`addon-${plan.id}-${addon.id}`" class="flex items-center gap-2.5 p-1.5 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors duration-200">
                    <!-- Custom beautiful checkbox -->
                    <div class="relative">
                      <input
                        type="checkbox"
                        :id="`addon-${plan.id}-${addon.id}`"
                        :value="addon.id"
                        :checked="selectedAddonsPerPlan[plan.id]?.includes(addon.id)"
                        @change="e => {
                          const checked = e.target.checked;
                          const arr = selectedAddonsPerPlan[plan.id] ? [...selectedAddonsPerPlan[plan.id]] : [];
                          if (checked) {
                            if (!arr.includes(addon.id)) arr.push(addon.id);
                          } else {
                            const idx = arr.indexOf(addon.id);
                            if (idx > -1) arr.splice(idx, 1);
                          }
                          selectedAddonsPerPlan[plan.id] = arr;
                        }"
                        class="sr-only"
                      />
                      <div class="w-4 h-4 rounded border-2 transition-all duration-200 flex items-center justify-center"
                           :class="{ 
                             'border-blue-500 bg-blue-500 shadow-sm': selectedAddonsPerPlan[plan.id]?.includes(addon.id),
                             'border-gray-300 bg-white group-hover/addon:border-blue-300': !selectedAddonsPerPlan[plan.id]?.includes(addon.id)
                           }">
                        <svg v-if="selectedAddonsPerPlan[plan.id]?.includes(addon.id)" 
                             class="w-2.5 h-2.5 text-white" 
                             fill="currentColor" 
                             viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                        </svg>
                      </div>
                    </div>
                    
                    <span class="text-[12px] font-medium text-gray-700 group-hover/addon:text-gray-900 transition-colors duration-200 truncate">
                      {{ addon.name }}
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      <div v-if="!isPending" class="">
        <button
          class="bg-white p-2 border border-gray-200 rounded-full size-8 flex justify-center items-center cursor-pointer absolute -left-3 top-1/2"
          type="button"
          @click="scrollLeft"
        >
          <LeftArrow />
        </button>
        <button
          class="bg-white p-2 border border-gray-200 rounded-full size-8 flex justify-center items-center cursor-pointer absolute -right-3 top-1/2"
          type="button"
          @click="scrollRight"
        >
          <RightArrow />
        </button>
      </div>
    </div>
    <Modal :isOpen="showSubscribeModal" @close="showSubscribeModal = false" title="Subscribe Plan">
      <div v-if="selectedPlan">
        <h2 class="font-bold mb-2">{{ selectedPlan.name }}</h2>
        <p>{{ selectedPlan.description }}</p>
        <p class="mb-2">Price: ${{ selectedPlan.price }}</p>
        <div v-if="selectedAddons.length" class="mb-4">
          <h3 class="font-semibold mb-2">Selected Add-ons:</h3>
          <ul class="list-disc list-inside">
            <li v-for="addonId in selectedAddons" :key="addonId" class="text-sm">
              {{ selectedPlan.addons?.find(a => a.id === addonId)?.name }}
            </li>
          </ul>
        </div>
        <button
          class="primaryButton mt-4 w-full"
          :disabled="isSubscribing"
          @click="handleSubscribe"
        >
          {{ isSubscribing ? 'Subscribing...' : 'Confirm Subscribe' }}
        </button>
      </div>
    </Modal>
  </DashboardLayouts>
</template>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.authEllipseTop {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0rem;
  right: -2rem;
  display: none;
  z-index: 0;
}
</style>
